/**
 * Ingredient Matching Row Component
 *
 * Single row showing an extracted ingredient with:
 * - Original text (readonly)
 * - Match status badge
 * - Ingredient select (for existing ingredients)
 * - Quantity and unit inputs
 * - Optional toggle
 * - New ingredient form (name/category) when is_new=true
 */

import type { ExtractedIngredient } from '@/types/recipeImport';
import type { Ingredient } from '@/types/food';
import { MatchStatusBadge } from './MatchStatusBadge';

interface IngredientMatchingRowProps {
  ingredient: ExtractedIngredient;
  availableIngredients: Ingredient[];
  onChange: (updated: ExtractedIngredient) => void;
}

/**
 * Common unit options
 */
const UNIT_OPTIONS = ['g', 'kg', 'ml', 'l', 'pcs', 'tbsp', 'tsp', 'cup', 'oz', 'lb', 'to taste', 'serving'];

/**
 * Common category options for new ingredients
 */
const CATEGORY_OPTIONS = ['pantry', 'dairy', 'meat', 'produce', 'grains', 'spices', 'other'];

export function IngredientMatchingRow({
  ingredient,
  availableIngredients,
  onChange,
}: IngredientMatchingRowProps) {
  const handleFieldChange = (
    field: keyof ExtractedIngredient,
    value: string | number | boolean | null
  ) => {
    onChange({ ...ingredient, [field]: value });
  };

  const handleIngredientSelect = (ingredientId: number) => {
    const selectedIngredient = availableIngredients.find(ing => ing.id === ingredientId);

    onChange({
      ...ingredient,
      matched_ingredient_id: ingredientId || null,
      matched_ingredient_name: selectedIngredient?.name || null,
      is_new: false,
    });
  };

  const handleNewIngredientToggle = () => {
    if (ingredient.is_new) {
      // Switching from new to existing - try to find a match
      onChange({
        ...ingredient,
        is_new: false,
        matched_ingredient_id: null,
        matched_ingredient_name: null,
      });
    } else {
      // Switching from existing to new
      onChange({
        ...ingredient,
        is_new: true,
        matched_ingredient_id: null,
        matched_ingredient_name: null,
      });
    }
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-3">
      {/* Header Row: Original Text + Badge */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <div className="text-xs font-medium text-gray-500 mb-1">Original Text</div>
          <div className="text-sm text-gray-900 font-medium">{ingredient.original_text}</div>
        </div>
        <MatchStatusBadge confidence={ingredient.confidence} isNew={ingredient.is_new} />
      </div>

      {/* Toggle: Existing vs New Ingredient */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleNewIngredientToggle}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          {ingredient.is_new ? '← Use existing ingredient' : 'Create new ingredient →'}
        </button>
      </div>

      {/* Conditional Form: Existing Ingredient */}
      {!ingredient.is_new && (
        <div className="grid grid-cols-[1fr_100px_100px_80px] gap-3">
          {/* Ingredient Select */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Ingredient</label>
            <select
              value={ingredient.matched_ingredient_id || ''}
              onChange={(e) => handleIngredientSelect(Number(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
              aria-label={`Select ingredient for "${ingredient.original_text}"`}
            >
              <option value="">Select ingredient</option>
              {availableIngredients.map((ing) => (
                <option key={ing.id} value={ing.id}>
                  {ing.name}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Quantity</label>
            <input
              type="number"
              value={ingredient.quantity ?? ''}
              onChange={(e) =>
                handleFieldChange(
                  'quantity',
                  e.target.value ? Number(e.target.value) : null
                )
              }
              placeholder="0"
              min="0"
              step="any"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
              aria-label={`Quantity for "${ingredient.original_text}"`}
            />
          </div>

          {/* Unit */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Unit</label>
            <select
              value={ingredient.unit || ''}
              onChange={(e) => handleFieldChange('unit', e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
              aria-label={`Unit for "${ingredient.original_text}"`}
            >
              <option value="">-</option>
              {UNIT_OPTIONS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          {/* Optional Checkbox */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Optional</label>
            <div className="flex items-center h-[38px]">
              <input
                type="checkbox"
                checked={ingredient.optional}
                onChange={(e) => handleFieldChange('optional', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label={`Mark "${ingredient.original_text}" as optional`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Conditional Form: New Ingredient */}
      {ingredient.is_new && (
        <div className="space-y-3">
          {/* Name and Category */}
          <div className="grid grid-cols-[1fr_150px] gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Ingredient Name</label>
              <input
                type="text"
                value={ingredient.extracted_name}
                onChange={(e) => handleFieldChange('extracted_name', e.target.value)}
                placeholder="Enter ingredient name"
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                aria-label={`Name for new ingredient "${ingredient.original_text}"`}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Category</label>
              <select
                value={ingredient.suggested_category || ''}
                onChange={(e) => handleFieldChange('suggested_category', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
                aria-label={`Category for new ingredient "${ingredient.original_text}"`}
              >
                <option value="">Select category</option>
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quantity, Unit, Optional */}
          <div className="grid grid-cols-[100px_100px_80px] gap-3">
            {/* Quantity */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Quantity</label>
              <input
                type="number"
                value={ingredient.quantity ?? ''}
                onChange={(e) =>
                  handleFieldChange(
                    'quantity',
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                placeholder="0"
                min="0"
                step="any"
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                aria-label={`Quantity for new ingredient "${ingredient.original_text}"`}
              />
            </div>

            {/* Unit */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Unit</label>
              <select
                value={ingredient.unit || ''}
                onChange={(e) => handleFieldChange('unit', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
                aria-label={`Unit for new ingredient "${ingredient.original_text}"`}
              >
                <option value="">-</option>
                {UNIT_OPTIONS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            {/* Optional Checkbox */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700">Optional</label>
              <div className="flex items-center h-[38px]">
                <input
                  type="checkbox"
                  checked={ingredient.optional}
                  onChange={(e) => handleFieldChange('optional', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  aria-label={`Mark new ingredient "${ingredient.original_text}" as optional`}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
